import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProductHeader from "../../components/manage-product-component/product-header";
import BasicProductDetails from "../../components/manage-product-component/basic-product-details";
import ProductMedia from "../../components/manage-product-component/product-media";
import ProductMeta from "../../components/manage-product-component/product-meta";
import VariantsSection from "../../components/manage-product-component/variant-section";
import ProductActions from "../../components/manage-product-component/product-action";
import { productService } from "../../utils/services/productService";
import { imageService } from "../../utils/services/imageService";
import { supplierService } from "../../utils/services/supplierService";
import buildQuery from "../../utils/query-builder";
import config from "../../config";
import { Box, CircularProgress, Divider, Grid } from "@mui/material";

const ManageProduct = () => {
  const initialData = {
    name: "",
    short_description: "",
    pga_flag: "",
    url: "",
    description: "",
    images: [],
    status: "",
    product_type: "",
    category_id: null,
    variants: [],
    currency: "",
    vendor: null,
    client_id: null,
  };

  const [productDetails, setProductDetails] = useState(initialData);
  const [variants, setVariants] = useState([]);
  const [selectedClient, setSelectedClient] = useState({ name: "" });
  const [vendor, setVendor] = useState(null);
  const [productId, setProductId] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [coverImageIndex, setCoverImageIndex] = useState(-1);
  const [imageUrl, setImageUrl] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [hsCodeErrors, setHsCodeErrors] = useState({});
  const [htsCodeErrors, setHtsCodeErrors] = useState({});
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const clientId = localStorage.getItem("client_id");

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
  }, []);

  const fetchImage = (image) =>
    image?.url
      ? image.url
      : image?.image
        ? `${config.baseURL}/fetch-file/${image.image}`
        : "/product.png";

  const fetchProductId = async (id) => {
    setLoading(true);
    try {
      const response = await productService.fetchById(id);
      setSelectedProduct(response);
      setProductDetails(JSON.parse(JSON.stringify(response)));
    } catch (error) {
      console.error("Fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const idFromURL = window.location.href.split("/").pop();
    setProductId(idFromURL);
    if (idFromURL !== "create") fetchProductId(idFromURL);
  }, []);

  useEffect(() => {
    if (!selectedProduct || productId === "create") return;

    setProductDetails((prev) => ({
      ...prev,
      ...JSON.parse(JSON.stringify(selectedProduct)),
    }));

    const fetched = selectedProduct.images.map(fetchImage);
    setSelectedImages(
      selectedProduct.images.map((img) => ({
        image: img.image,
        feature_image: img.feature_image,
        url: img.url,
      })),
    );
    setImageUrls(fetched);
    setCoverImageIndex(
      selectedProduct.images.findIndex((img) => img.feature_image),
    );

    const parentVariants = selectedProduct.variants.filter((v) => !v.parent);
    setVariants(
      parentVariants.map((variant) => ({
        ...variant,
        subSkus: selectedProduct.variants
          .filter((s) => s.parent === variant.uid)
          .map((subSku) => ({
            ...subSku,
            sku: variant.isBundle ? { sku: subSku.sku } : subSku.sku,
          })),
      })),
    );

    if (selectedProduct.vendor?._id) {
      setVendor(selectedProduct.vendor);
    }

    // Set client
    if (selectedProduct.client_id?._id) {
      setSelectedClient({
        name:
          selectedProduct?.client_id?.business_name ||
          selectedProduct?.client_id?.name ||
          "",
        _id: selectedProduct.client_id?._id,
      });
    }
  }, [selectedProduct, productId]);

  return (
    <Box
      sx={{
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {loading && (
        <Box className="loading-overlay">
          <CircularProgress color="inherit" />
        </Box>
      )}

      <ProductHeader productId={productId} />
      <Divider sx={{ mb: 2 }} />

      <Box ref={containerRef} sx={{ flexGrow: 1, overflowY: "auto", px: 2 }}>
        <Grid container spacing={2}>
          <BasicProductDetails
            {...{
              productDetails,
              setProductDetails,
              clientId,
              setSelectedClient,
              selectedClient,
              formErrors,
              setFormErrors,
            }}
          />

          <ProductMedia
            {...{
              selectedImages,
              setSelectedImages,
              imageUrls,
              setImageUrls,
              coverImageIndex,
              setCoverImageIndex,
              imageUrl,
              setImageUrl,
              selectedProduct,
              setSelectedProduct,
            }}
          />

          <ProductMeta
            {...{
              productDetails,
              setProductDetails,
              supplierService,
              buildQuery,
              setVendor,
              selectedClient,
              formErrors,
              setFormErrors,
            }}
          />

          <VariantsSection
            {...{
              variants,
              setVariants,
              formErrors,
              setFormErrors,
            }}
          />

          <ProductActions
            {...{
              productDetails,
              setProductDetails,
              selectedClient,
              setSelectedClient,
              vendor,
              variants,
              imageUrl,
              clientId,
              navigate,
              selectedImages,
              selectedProduct,
              setSelectedProduct,
              setImageUrl,
              setVariants,
              initialData,
              setVendor,
              setFormErrors,
              formErrors,
            }}
          />
        </Grid>
      </Box>
    </Box>
  );
};

export default ManageProduct;
